import {
  MaterialReactTable,
  MRT_ColumnDef,
  useMaterialReactTable,
} from 'material-react-table';
import { FC, useMemo } from 'react';
import { SectionHeadingGroup } from '@/components/ui/section-heading-group';
import { StatusBadge } from '@/components/ui/status-badge';
import { useAssociatedDevicesInCurrentUnit } from '@/hooks/use-devices';
import { usePolicies } from '@/hooks/use-policies';
import {
  getSkillDriftSummary,
  SkillDriftSummary,
} from '@/lib/policies/get-skill-drift-summary';
import { composeTableOptions } from '@/table';
import { AlexaSkillPolicy } from '@/types/alexa-skill-policy';

type SkillDriftRow = {
  skill: AlexaSkillPolicy;
  summary: SkillDriftSummary;
};

const columns: MRT_ColumnDef<SkillDriftRow>[] = [
  {
    accessorKey: 'skill.skillName',
    header: 'Skill Name',
    grow: true,
    size: 200,
  },
  {
    accessorKey: 'skill.skillId',
    header: 'Skill ID',
    size: 250,
  },
  {
    id: 'configured',
    header: 'Configured',
    size: 150,
    Cell: ({ row }) => {
      const { enabled, stage } = row.original.skill;
      return (
        <div className="flex items-center gap-2">
          <StatusBadge
            text={enabled ? 'Enabled' : 'Disabled'}
            variant={enabled ? 'success' : 'alert'}
          />
          <span className="text-xs text-gray-500">{stage}</span>
        </div>
      );
    },
  },
  {
    id: 'drift',
    header: 'Device State',
    size: 200,
    Cell: ({ row }) => {
      const { reportingCount, divergentCount, alexaDeviceCount } =
        row.original.summary;

      if (alexaDeviceCount === 0) {
        return <span className="text-xs text-gray-500 italic">No devices</span>;
      }

      if (reportingCount === 0) {
        return (
          <span className="text-xs text-gray-500 italic">
            No devices reporting
          </span>
        );
      }

      if (divergentCount === 0) {
        return (
          <StatusBadge
            text={`${reportingCount} of ${alexaDeviceCount} in sync`}
            variant="success"
          />
        );
      }

      return (
        <StatusBadge
          text={`${divergentCount} of ${reportingCount} drifted`}
          variant="warning"
        />
      );
    },
  },
];

export const DeviceFunctionsView: FC = () => {
  const { data: policy, isLoading: isLoadingPolicy } = usePolicies();
  const { data: devices, isLoading: isLoadingDevices } =
    useAssociatedDevicesInCurrentUnit();

  const rows = useMemo<SkillDriftRow[]>(() => {
    if (!policy) return [];
    return policy.alexaSkills.map((skill) => ({
      skill,
      summary: getSkillDriftSummary(skill.skillId, devices),
    }));
  }, [policy, devices]);

  const isLoading = isLoadingPolicy || isLoadingDevices;

  const table = useMaterialReactTable({
    ...composeTableOptions<SkillDriftRow>({ enableRowActions: false }),
    columns,
    enableColumnResizing: true,
    data: rows,
    state: {
      isLoading,
      ...(isLoading
        ? {
            pagination: {
              pageIndex: 0,
              pageSize: 1,
            },
          }
        : {}),
    },
  });

  return (
    <div>
      <SectionHeadingGroup
        className="mb-6"
        subtitle="Skill configurations for Alexa-enabled devices in this unit."
        title="Device Functions"
      />
      <MaterialReactTable table={table} />
    </div>
  );
};
